
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Building2, Loader, CheckCircle, AlertCircle } from 'lucide-react';

const AuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const handleEmailVerification = async () => {
      try {
        console.log('🔄 Processing email verification callback...');
        
        // Get verification parameters from URL
        const token = searchParams.get('token');
        const type = searchParams.get('type');
        const email = searchParams.get('email');
        const role = searchParams.get('role') || 'landlord';
        
        console.log('📧 Verification params:', { token, type, email, role });
        
        if (type === 'signup' && token) {
          // Verify the email token without creating a session
          const { error } = await supabase.auth.verifyOtp({
            token_hash: token,
            type: 'signup'
          });
          
          if (error) {
            console.error('❌ Email verification failed:', error);
            setErrorMessage(error.message);
            setStatus('error');
            return;
          }
          
          console.log('✅ Email verified successfully');
          setStatus('success');
          
          // Immediately sign out any auto-created session
          await supabase.auth.signOut();
          
          // Redirect to email verified page after a brief delay
          setTimeout(() => {
            navigate(`/email-verified?role=${role}&verified=true`, { replace: true });
          }, 1500);
          
        } else {
          // Handle other auth callback types or missing parameters
          console.log('🔄 Handling other auth callback or redirecting...');
          const role = searchParams.get('role') || 'landlord';
          navigate(`/email-verified?role=${role}&verified=true`, { replace: true });
        }
        
      } catch (error: any) {
        console.error('💥 Unexpected error during verification:', error);
        setErrorMessage('An unexpected error occurred during verification');
        setStatus('error');
      }
    };

    handleEmailVerification();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="shadow-lg border border-border bg-card">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <Building2 className="h-8 w-8 text-primary" />
              <span className="text-2xl font-bold text-foreground">RentFlow</span>
            </div>
            
            <div className="mx-auto mb-4 w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
              {status === 'verifying' && <Loader className="h-8 w-8 text-primary animate-spin" />}
              {status === 'success' && <CheckCircle className="h-8 w-8 text-green-600" />}
              {status === 'error' && <AlertCircle className="h-8 w-8 text-red-600" />}
            </div>
            
            <CardTitle className="text-xl sm:text-2xl font-bold text-foreground">
              {status === 'verifying' && 'Verifying your email...'}
              {status === 'success' && 'Email verified!'}
              {status === 'error' && 'Verification failed'}
            </CardTitle>
          </CardHeader>
          
          <CardContent className="text-center">
            {status === 'verifying' && (
              <p className="text-muted-foreground">
                Please wait while we verify your email address.
              </p>
            )}
            
            {status === 'success' && (
              <div className="space-y-3">
                <p className="text-green-700 font-medium">
                  Your email has been successfully verified!
                </p>
                <p className="text-sm text-muted-foreground">
                  Redirecting you to the sign in page...
                </p>
              </div>
            )}
            
            {status === 'error' && (
              <div className="space-y-3">
                <p className="text-red-700 font-medium">
                  There was an issue verifying your email.
                </p>
                {errorMessage && (
                  <p className="text-sm text-red-600">
                    {errorMessage}
                  </p>
                )}
                <p className="text-sm text-muted-foreground">
                  You can try signing up again or contact support if the issue persists.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AuthCallback;
